import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import {
	Button,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Checkbox,
	Link,
	CircularProgress,
	Divider,
	Box,
} from '@mui/material';
import { RnDDialog } from 'components';

import { ROUTES_PATH_SCREENMANAGER_SLIDES, getRouteScreenActAs } from 'routes';
import { fetchUserByDomain, searchplaylists, fetchSelectedPlaylist } from 'restful';
import { encryptStr } from 'utils/appHelper';

const ListOfUserPlaylist = ({
	mediaFileValue,
	onClose,
	playlistFilters,
	onHandleContinue,
	dialogState,
}) => {
	const intl = useIntl();

	const [userPlaylistMap, setUserPlaylistMap] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedPlaylists, setSelectedPlaylists] = useState({});
	const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
	const fetchUsers = React.useCallback(
		async (uids) => {
			let queryObj = dialogState.readOnly
				? { userIds: uids }
				: {
						active: true,
						screenplayer: 'bronze',
						...(playlistFilters.selectedGroups?.length && {
							userGroupIds: playlistFilters.selectedGroups.join(','),
						}),
				  };
			try {
				const response = await fetchUserByDomain({
					queryParams: queryObj,
				});
				return response.data.results;
			} catch (error) {
				console.error('Error fetching users:', error);
				return []; // Return empty data on error
			}
		},
		[dialogState.readOnly, playlistFilters.selectedGroups]
	);

	const fetchPlaylists = React.useCallback(
		async (createdByUids) => {
			const payload = {
				createdByUids,
				departmentIds: [playlistFilters.selectedDepartment],
				isMaster: false,
				layout: [playlistFilters.orientation],
				...(playlistFilters.playlistName?.trim() && { name: playlistFilters.playlistName.trim() }),
			};
			try {
				const response = await searchplaylists({
					queryParams: {
						limit: 250,
					},
					bodyParams: {
						filters: payload,
					},
				});
				return { data: response.data };
			} catch (error) {
				console.error('Error fetching playlists:', error);
				return { data: [] }; // Return empty data on error
			}
		},
		[playlistFilters.orientation, playlistFilters.playlistName, playlistFilters.selectedDepartment]
	);
	const mapUsersWithPlaylists = (users, playlists) => {
		const userMap = new Map(users.map((u) => [u.id.toString(), u]));
		const grouped = playlists.reduce((acc, p) => {
			if (!p.createdByUid || !userMap.has(p.createdByUid)) return acc;
			if (!acc[p.createdByUid]) acc[p.createdByUid] = [];
			acc[p.createdByUid].push(p);
			return acc;
		}, {});
		return Object.entries(grouped).map(([uid, pl]) => ({ user: userMap.get(uid), playlists: pl }));
	};

	const sortedUserPlaylistMap = React.useMemo(() => {
		return [...userPlaylistMap].sort((a, b) => {
			const nameA = a.user.company?.toLowerCase() || '';
			const nameB = b.user.company?.toLowerCase() || '';
			return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
		});
	}, [userPlaylistMap, sortOrder]);

	const handleCheckboxToggle = (playlistId, checked) => {
		setSelectedPlaylists((prev) => ({ ...prev, [playlistId]: checked }));
	};

	React.useEffect(() => {
		if (!dialogState?.open || !mediaFileValue?.id) return;
		const fetchData = async (dialogStateArg) => {
			try {
				setLoading(true);
				let usersData = [];
				let playlistData = [];

				if (dialogStateArg.readOnly) {
					const playlistsResponse = await fetchSelectedPlaylist({
						mediafileId: mediaFileValue.id,
						queryParams: { scope: 'domain' },
					});
					playlistData = playlistsResponse?.data?.playlists || [];
					const uniqueUids = [...new Set(playlistData.map((p) => p.createdByUid))].join(',');
					usersData = await fetchUsers(uniqueUids);
				} else {
					usersData = await fetchUsers();
					const uniqueUsers = [...new Set(usersData.map((user) => user.id.toString()))];
					const playlistsResponse = await fetchPlaylists(uniqueUsers);
					playlistData = Array.isArray(playlistsResponse.data) ? playlistsResponse.data : [];
				}

				const mapped = mapUsersWithPlaylists(usersData, playlistData);
				setUserPlaylistMap(mapped);
				const initialSelection = {};
				mapped.forEach(({ playlists }) => {
					playlists.forEach((p) => {
						initialSelection[p.id] = true;
					});
				});
				setSelectedPlaylists(initialSelection);
			} catch (err) {
				console.error('Failed to fetch:', err);
			} finally {
				setLoading(false);
			}
		};

		fetchData(dialogState);
	}, [dialogState, mediaFileValue?.id, fetchPlaylists, fetchUsers]);

	return (
		<RnDDialog
			open={dialogState.open}
			size="md"
			title={intl.formatMessage({
				id: dialogState.readOnly
					? 'pages.MediaFiles.components.BulkAddToScreens.ListOfUserPlaylistTitle2'
					: 'pages.MediaFiles.components.BulkAddToScreens.ListOfUserPlaylistTitle1',
			})}
			showFullScreen={true}
			onClose={onClose}
			onCloseText={dialogState.readOnly ? 'Ok' : 'Cancel'}
			onActions={['Continue'].map((item, index) => {
				return (
					!dialogState.readOnly &&
					userPlaylistMap.length > 0 && (
						<Button
							key={index}
							onClick={() => {
								onHandleContinue(selectedPlaylists);
							}}
							variant="contained"
							color="primary"
							disabled={!Object.values(selectedPlaylists).some(Boolean)}
						>
							{item}
						</Button>
					)
				);
			})}
		>
			{loading ? (
				<CircularProgress />
			) : userPlaylistMap.length > 0 ? (
				<>
					{!dialogState.readOnly && (
						<Typography variant="subtitle1" sx={{ mb: 2 }}>
							Please confirm the following{' '}
							<b>{Object.values(selectedPlaylists).filter(Boolean).length}</b> playlists:
						</Typography>
					)}
					<Divider sx={{ mb: 0 }} />
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>
									<b>User Name</b>
								</TableCell>
								<TableCell
									onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
									sx={{ cursor: 'pointer', userSelect: 'none' }}
								>
									<b>Company {sortOrder === 'asc' ? '↑' : '↓'}</b>
								</TableCell>
								<TableCell sx={{ pl: 0 }}>
									{!dialogState.readOnly && (
										<Checkbox
											sx={{ p: 1, pr: 1, verticalAlign: 'middle' }}
											checked={
												Object.values(selectedPlaylists).length > 0 &&
												Object.values(selectedPlaylists).every(Boolean)
											}
											indeterminate={
												Object.values(selectedPlaylists).some(Boolean) &&
												!Object.values(selectedPlaylists).every(Boolean)
											}
											onChange={(e) => {
												const newSelected = {};
												userPlaylistMap.forEach(({ playlists }) => {
													playlists.forEach((p) => {
														newSelected[p.id] = e.target.checked;
													});
												});
												setSelectedPlaylists(newSelected);
											}}
										/>
									)}
									<b style={{ marginLeft: 8 }}>Playlists</b>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{sortedUserPlaylistMap.map(({ user, playlists }) => {
								if (playlists.length === 0) return null;
								return playlists.map((playlist, index) => (
									<TableRow key={playlist.id || `${user.id}-${index}`}>
										{index === 0 && (
											<>
												<TableCell
													rowSpan={playlists.length}
													sx={{
														verticalAlign: 'top',
														maxWidth: 200,
														wordWrap: 'break-word',
														whiteSpace: 'normal',
													}}
												>
													<Link
														target="_blank"
														rel="noopener"
														onClick={() => {
															window.location.assign(getRouteScreenActAs(user.email, user.id));
														}}
													>
														{user.firstname} {user.lastname}
													</Link>
												</TableCell>
												<TableCell rowSpan={playlists.length} sx={{ verticalAlign: 'top' }}>
													{user.company}
												</TableCell>
											</>
										)}
										<TableCell
											sx={{
												padding: '10px',
											}}
										>
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													flexDirection: { xs: 'column', sm: 'row' },
													gap: 1,
												}}
											>
												{!dialogState.readOnly && (
													<Checkbox
														checked={!!selectedPlaylists[playlist.id]}
														onChange={(e) => handleCheckboxToggle(playlist.id, e.target.checked)}
														sx={{ p: 0 }}
													/>
												)}
												<Link
													href={
														ROUTES_PATH_SCREENMANAGER_SLIDES +
														`?playlistId=${encryptStr(playlist.id)}`
													}
													target="_blank"
													rel="noopener"
													underline="hover"
												>
													{playlist.name}
												</Link>
											</Box>
										</TableCell>
									</TableRow>
								));
							})}
						</TableBody>
					</Table>
				</>
			) : (
				<Typography>No suitable playlists found.</Typography>
			)}
		</RnDDialog>
	);
};

ListOfUserPlaylist.propTypes = {
	dialogState: PropTypes.shape({
		open: PropTypes.bool.isRequired,
		readOnly: PropTypes.bool.isRequired,
	}).isRequired,
	onClose: PropTypes.func.isRequired,
	onHandleContinue: PropTypes.func,
	playlistFilters: PropTypes.object,
	mediaFileValue: PropTypes.object.isRequired,
};
ListOfUserPlaylist.defaultProps = {
	playlistFilters: {},
};
export default ListOfUserPlaylist;
